import { useState } from "react";
import { LayoutDashboard, CreditCard, Inbox, LogOut, Menu, X, Users, Building2, Megaphone, Dumbbell, Video, BookOpen, Wallet, ListChecks, Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/components/site/assets";
import OverviewTab from "@/components/platform/OverviewTab";
import MembershipsTab from "@/components/platform/MembershipsTab";
import BillingTab from "@/components/platform/BillingTab";
import LeadsTab from "@/components/platform/LeadsTab";
import WaitlistTab from "@/components/platform/WaitlistTab";
import AthletesTab from "@/components/platform/AthletesTab";
import UsersTab from "@/components/platform/UsersTab";
import OrganizationsTab from "@/components/platform/OrganizationsTab";
import AnnouncementsTab from "@/components/platform/AnnouncementsTab";
import VideoReviewTab from "@/components/platform/VideoReviewTab";
import ResourcesTab from "@/components/platform/ResourcesTab";
import TrackAdmin from "@/components/track/TrackAdmin";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "athletes", label: "Athletes", icon: Dumbbell },
  { key: "videos", label: "Video Review", icon: Video },
  { key: "resources", label: "Resources", icon: BookOpen },
  { key: "users", label: "Users", icon: Users },
  { key: "organizations", label: "Organizations", icon: Building2 },
  { key: "memberships", label: "Memberships", icon: CreditCard },
  { key: "billing", label: "Billing", icon: Wallet },
  { key: "announcements", label: "Announcements", icon: Megaphone },
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "waitlist", label: "Waitlist", icon: ListChecks },
  { key: "trackos", label: "Origin Track OS", icon: Activity },
];

export default function Platform() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavLinks = () => (
    <nav className="space-y-1">
      {NAV.map((n) => {
        const Icon = n.icon;
        const active = tab === n.key;
        return (
          <button
            key={n.key}
            data-testid={`nav-${n.key}`}
            onClick={() => { setTab(n.key); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-widest font-bold border-l-2 transition-colors ${
              active ? "border-white bg-white/5 text-white" : "border-transparent text-white/45 hover:text-white hover:bg-white/[0.03]"
            }`}
          >
            <Icon size={16} /> {n.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex" data-testid="platform-page">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/10 bg-black fixed inset-y-0">
        <div className="h-20 flex items-center gap-3 px-6 border-b border-white/10">
          <img src={LOGO} alt="JJ Speed Factory" className="h-9 w-auto object-contain" />
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 border-l border-white/20 pl-3">
            Platform
          </span>
        </div>
        <div className="flex-1 py-6"><NavLinks /></div>
        <div className="border-t border-white/10 p-4">
          <p className="text-white/40 text-xs truncate mb-3" data-testid="platform-user-email">{user?.email}</p>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full inline-flex items-center justify-center gap-2 border border-white/20 px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"
          >
            <LogOut size={14} /> Log Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-20 h-16 bg-black border-b border-white/10 flex items-center justify-between px-4">
        <img src={LOGO} alt="JJ Speed Factory" className="h-8 w-auto object-contain" />
        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((v) => !v)} className="text-white p-2">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed top-16 inset-x-0 z-20 bg-black border-b border-white/10 py-4">
          <NavLinks />
          <div className="px-4 pt-4 mt-2 border-t border-white/10">
            <p className="text-white/40 text-xs mb-3">{user?.email}</p>
            <button data-testid="logout-btn-mobile" onClick={logout} className="w-full inline-flex items-center justify-center gap-2 border border-white/20 px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">
              <LogOut size={14} /> Log Out
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 lg:py-14">
          {tab === "overview" && <OverviewTab />}
          {tab === "athletes" && <AthletesTab />}
          {tab === "videos" && <VideoReviewTab />}
          {tab === "resources" && <ResourcesTab />}
          {tab === "users" && <UsersTab />}
          {tab === "organizations" && <OrganizationsTab />}
          {tab === "memberships" && <MembershipsTab />}
          {tab === "billing" && <BillingTab />}
          {tab === "announcements" && <AnnouncementsTab />}
          {tab === "leads" && <LeadsTab />}
          {tab === "waitlist" && <WaitlistTab />}
          {tab === "trackos" && <TrackAdmin />}
        </div>
      </main>
    </div>
  );
}
